import SyncEncounters from '@/mixins/SyncEncounters'
import compact from 'lodash/compact'
import { mapState } from 'vuex'

export default {
  mixins: [SyncEncounters],

  computed: {
    ...mapState('encounters', { encounters: 'items' }),
  },

  methods: {
    /**
     * Send the communication log (main sync process)
     * @param {Array} encountersToSync - Encounters to sync
     * @param {Array} patientsToSync - Patients to sync
     * @param {Object} commLog - Communication log payload
     */
    async sendCommunicationLog(encountersToSync, patientsToSync, commLog) {
      let message = ''
      let response = ''

      try {
        this.$store.commit('auth/updateQuickEntryEmails', {
          id: commLog.place_of_service_id,
          email_addresses: commLog.contact_email,
        })

        // Prep for sync log
        if (patientsToSync[0]) {
          patientsToSync[0].sync_log = this.syncLogSetup('Send Comm Log: Post Core Patients')
        }
        if (encountersToSync[0]) {
          encountersToSync[0].sync_log = this.syncLogSetup('Send Comm Log: Post Encounters')
        }
        commLog.sync_log = this.syncLogSetup('Send Comm Log: Post Comm Log')

        // Reset the sync progress values
        this.syncPos = 0
        this.syncCnt = 5

        // Start the syncing process
        this.syncingProgress()
        await this.$store.dispatch('encounters/syncingModal', true)
        console.log('----------- Comm Log Starting -----------')

        // Syncing Core Patients
        message = 'Patients Core'
        const patientCoreResponse = await this.syncCorePatients(patientsToSync)
        if (patientCoreResponse !== true) {
          response += patientCoreResponse
          response += '<hr><br>'
        }
        this.syncingProgress()
        console.log(`Syncing done for ${message}`)

        // Syncing Encounters
        message = 'Encounters'
        const encounterResponse = await this.syncEncounters(encountersToSync)
        if (encounterResponse !== true) {
          response += encounterResponse
          response += '<hr><br>'
        }
        this.syncingProgress()
        console.log(`Syncing done for ${message}`)

        // Syncing Full Patients
        message = 'Full Patients'
        if (patientsToSync[0]) {
          patientsToSync[0].sync_log = this.syncLogSetup('Send Comm Log: Post Full Patients')
        }
        const patientFullResponse = await this.syncFullPatients(patientsToSync)
        if (patientFullResponse !== true) {
          response += patientFullResponse
          response += '<hr><br>'
        }
        this.syncingProgress()
        console.log(`Syncing done for ${message}`)

        // Throw error if any sync failed before sending comms log
        if (response !== '') {
          throw response
        }

        // Sending Comms Log
        message = 'Communication Log'
        this.$store.commit('encounters/SET_SYNCING_TXT', 'Sending Communication Log')
        const commLogResponse = await this.$store.dispatch('encounters/sendCommLog', { ...commLog })

        if (commLogResponse === true) {
          this.syncingProgress()
          console.log('Done posting communication log...')

          // Pull updated facilities' passwords
          await this.$store.dispatch('facilities/loadFacilities')
          await this.$store.dispatch('auth/refresh').catch(error => {
            setTimeout(() => {
              this.$store.dispatch('notify', { value: error, color: 'error' })
            }, 1000)
          })

          console.log('---------- Comm Log Completed Successfully ----------')
          await this.$store.dispatch('notify', { value: 'Communication log sent.' })
          await this.logCleanup()
        } else {
          await this.$store.dispatch('encounters/setSynced', false)
          throw response
        }
      } catch (responseError) {
        response = `<strong>Failed to send communication log:</strong><br><br>${responseError}`
        await this.logCleanup()
        await this.errorDialog(response)
      }
    },

    /**
     * Resend an existing communication log
     */
    async resendCommunicationLog() {
      this.detailModalState = false

      const userId = this.$scribeProvider.id ?? this.$authUser.userId()
      let encountersToSync = []
      const patientsToSync = []
      const encounterIds = [this.logDetailEncounterId]

      encountersToSync.push(this.$store.getters['encounters/getById'](this.logDetailEncounterId))
      patientsToSync.push(this.$store.getters['patients/getById'](encountersToSync[0].patient_id))

      // Retrieve all unsynced encounters linked to the patient IDs in patientsToSync
      patientsToSync.forEach(patient => {
        const otherEncounters = this.encounters.filter(e =>
          e.created_by_user_id === userId &&
          !e.is_synced &&
          e.patient_id === patient.id
        )

        otherEncounters.forEach(otherEncounter => {
          encountersToSync.push(otherEncounter)
        })
      })

      // De-dupe encountersToSync
      encountersToSync = [...new Set(compact(encountersToSync))]

      // Create communication log object
      const commLog = {
        created_by_user_id: userId,
        place_of_service_id: this.logDetails.place_of_service_id,
        visit_date: this.logDetails.visit_date,
        log_type: 'Update',
        password: this.logDetails.password,
        contact_email: this.logDetails.contact_email,
        encounter_ids: encounterIds,
      }

      await this.sendCommunicationLog(encountersToSync, patientsToSync, commLog)
    },
  },
}